import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const DEFAULT_MARGIN = 15;

/**
 * Accepts a number or a partial {top,right,bottom,left} object and returns a
 * complete margin object in millimetres.
 */
function normaliseMargin(margin) {
  if (typeof margin === "number") {
    return { top: margin, right: margin, bottom: margin, left: margin };
  }

  const {
    top = DEFAULT_MARGIN,
    right = DEFAULT_MARGIN,
    bottom = DEFAULT_MARGIN,
    left = DEFAULT_MARGIN,
  } = margin || {};

  return { top, right, bottom, left };
}

/**
 * Captures a DOM element and saves it as a multi-page PDF.
 *
 * The element is rasterised with html2canvas, then the resulting bitmap is
 * sliced into page-height chunks so tall content paginates instead of running
 * off the bottom of page one.
 *
 * @param {HTMLElement|React.RefObject<HTMLElement>} target
 *        The element to capture, or a ref pointing at it.
 * @param {Object}  [options]
 * @param {string}  [options.filename="export.pdf"]
 * @param {number|{top?:number,right?:number,bottom?:number,left?:number}}
 *        [options.margin=15]
 *        Page margins in millimetres. A number applies to all four sides; an
 *        object sets them individually, with any omitted side defaulting to 15.
 * @param {number}  [options.scale=3]        Capture resolution multiplier.
 * @param {string}  [options.format="a4"]    jsPDF page format.
 * @param {string}  [options.orientation="portrait"]
 * @param {string}  [options.background="#ffffff"]
 * @param {number}  [options.widthRatio=1]   Fraction of the usable width the
 *                                           content should occupy, centred.
 * @param {"save"|"blob"|"open"} [options.output="save"]
 * @returns {Promise<Blob|void>} A Blob when output is "blob", otherwise void.
 */
export async function exportToPdf(target, options = {}) {
  const {
    filename = "export.pdf",
    margin = DEFAULT_MARGIN,
    scale = 3,
    format = "a4",
    orientation = "portrait",
    background = "#ffffff",
    widthRatio = 1,
    output = "save",
  } = options;

  const node = target && "current" in target ? target.current : target;

  if (!node) {
    throw new Error("exportToPdf: target element not found.");
  }

  // Webfonts that haven't loaded get captured as their fallback, which throws
  // off metrics and spacing. Wait them out where the browser supports it.
  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  const canvas = await html2canvas(node, {
    scale,
    backgroundColor: background,
    useCORS: true,
  });

  // html2canvas returns a zero-size canvas rather than throwing when it fails
  // (hidden element, transform on the capture root, exceeded canvas limits).
  if (!canvas.width || !canvas.height) {
    throw new Error(
      "exportToPdf: capture produced an empty canvas. Check that the " +
        "element is visible, carries no transform or opacity, and that the " +
        "content is not tall enough to exceed the browser's canvas limit.",
    );
  }

  const pdf = new jsPDF({ unit: "mm", format, orientation });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();

  const m = normaliseMargin(margin);

  const contentW = pageW - m.left - m.right;
  const usableW = contentW * widthRatio;
  const usableH = pageH - m.top - m.bottom;

  // Centre within the area the margins leave, so an asymmetric left/right pair
  // still reads as intentional rather than lopsided.
  const x = m.left + (contentW - usableW) / 2;

  // Conversion factor between canvas pixels and PDF millimetres. Everything
  // below depends on slice height and placed height agreeing via this value.
  const pxPerMm = canvas.width / usableW;
  const sliceHeightPx = Math.floor(usableH * pxPerMm);

  const slice = document.createElement("canvas");
  const ctx = slice.getContext("2d");

  let offset = 0;

  while (offset < canvas.height) {
    const chunkH = Math.min(sliceHeightPx, canvas.height - offset);

    slice.width = canvas.width;
    slice.height = chunkH;

    // Resizing clears the canvas, so the fill has to come after the assignment.
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(
      canvas,
      0,
      offset,
      canvas.width,
      chunkH,
      0,
      0,
      canvas.width,
      chunkH,
    );

    if (offset > 0) pdf.addPage();

    pdf.addImage(
      slice.toDataURL("image/png"),
      "PNG",
      x,
      m.top,
      usableW,
      chunkH / pxPerMm,
    );

    offset += chunkH;
  }

  if (output === "blob") return pdf.output("blob");
  if (output === "open") return pdf.output("dataurlnewwindow");

  return pdf.save(filename);
}

export default exportToPdf;
